import type { AdminTeam, AdminUser } from '@nexo/types';
import { Avatar, Badge, EmptyState, Modal, Spinner } from '@nexo/ui';
import { useQuery } from '@tanstack/react-query';
import { Building2, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatLongDate } from '../../lib/format';

const roleLabels = { owner: 'Propietario', admin: 'Admin', member: 'Miembro' } as const;
const roleTone = { owner: 'accent', admin: 'warning', member: 'neutral' } as const;

/** The teams a user belongs to, with their role (and custom roles) in each. */
export function UserTeamsDialog({
  user,
  onClose,
  onOpenTeam,
}: {
  user: AdminUser;
  onClose: () => void;
  onOpenTeam?: (team: { id: string; name: string }) => void;
}) {
  const { t } = useTranslation();
  const teams = useQuery({
    queryKey: ['admin', 'user-teams', user.id],
    queryFn: () => api.admin.userTeams(user.id),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={t('Equipos de {{name}}', { name: user.displayName })}
      description={user.email}
    >
      <DetailList
        loading={teams.isPending}
        empty={teams.data?.length === 0}
        emptyIcon={<Building2 className="size-8" />}
        emptyText={t('No pertenece a ningún equipo.')}
      >
        {teams.data?.map((team) => (
          <li key={team.id}>
            <Row
              avatar={<Avatar id={team.id} name={team.name} shape="square" size="sm" />}
              title={team.name}
              subtitle={
                team.memberCount === 1
                  ? t('1 miembro · desde el {{date}}', { date: formatLongDate(team.joinedAt) })
                  : t('{{count}} miembros · desde el {{date}}', {
                      count: team.memberCount,
                      date: formatLongDate(team.joinedAt),
                    })
              }
              role={team.role}
              customRoles={team.customRoles}
              onClick={onOpenTeam ? () => onOpenTeam(team) : undefined}
            />
          </li>
        ))}
      </DetailList>
    </Modal>
  );
}

/** The members of a team, owners and admins first. */
export function TeamMembersDialog({
  team,
  onClose,
}: {
  team: Pick<AdminTeam, 'id' | 'name'>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const members = useQuery({
    queryKey: ['admin', 'team-members', team.id],
    queryFn: () => api.admin.teamMembers(team.id),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={t('Miembros de {{name}}', { name: team.name })}
      description={
        members.data
          ? members.data.length === 1
            ? t('1 persona')
            : t('{{count}} personas', { count: members.data.length })
          : undefined
      }
    >
      <DetailList
        loading={members.isPending}
        empty={members.data?.length === 0}
        emptyIcon={<Users className="size-8" />}
        emptyText={t('El equipo no tiene miembros.')}
      >
        {members.data?.map((member) => (
          <li key={member.id}>
            <Row
              avatar={
                <Avatar id={member.id} name={member.displayName} src={member.avatarUrl} size="sm" />
              }
              title={member.displayName}
              subtitle={t('{{email}} · desde el {{date}}', {
                email: member.email,
                date: formatLongDate(member.joinedAt),
              })}
              role={member.role}
              customRoles={member.customRoles}
            />
          </li>
        ))}
      </DetailList>
    </Modal>
  );
}

function DetailList({
  loading,
  empty,
  emptyIcon,
  emptyText,
  children,
}: {
  loading: boolean;
  empty: boolean;
  emptyIcon: ReactNode;
  emptyText: string;
  children: ReactNode;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }
  if (empty) return <EmptyState icon={emptyIcon} title={emptyText} />;
  return (
    <ul className="scrollbar-thin -mx-2 flex max-h-[60vh] flex-col overflow-y-auto">{children}</ul>
  );
}

function Row({
  avatar,
  title,
  subtitle,
  role,
  customRoles,
  onClick,
}: {
  avatar: ReactNode;
  title: string;
  subtitle: string;
  role: keyof typeof roleLabels;
  customRoles: string[];
  onClick?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const content = (
    <>
      {avatar}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        <span className="block truncate text-xs text-muted">{subtitle}</span>
        {customRoles.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {customRoles.map((name) => (
              <span
                key={name}
                className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted"
              >
                {name}
              </span>
            ))}
          </span>
        )}
      </span>
      <Badge tone={roleTone[role]}>{t(roleLabels[role])}</Badge>
    </>
  );
  const className = 'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left';
  return onClick ? (
    <button type="button" onClick={onClick} className={`${className} hover:bg-surface-2`}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}
